import type { CreatedInvitation, InvitationDto, MemberDto, Role } from '@agent-platform/shared';
import { hasRole } from '@agent-platform/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { toast } from 'sonner';
import { CopyField } from '@/components/copy-field';
import { ErrorAlert } from '@/components/error-alert';
import { ListSearch, NoMatches, useListSearch } from '@/components/list-search';
import { PageHeader } from '@/components/page-header';
import { RequireTenant } from '@/components/require-tenant';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { api, errorMessage } from '@/lib/api';
import { formatDate, ROLE_LABELS } from '@/lib/format';
import { useMe, usePaged } from '@/lib/queries';

const ROLES: Role[] = ['owner', 'admin', 'viewer'];

function RoleSelect({
  value,
  onChange,
  disabled,
  label,
}: {
  value: Role;
  onChange: (r: Role) => void;
  disabled?: boolean;
  label: string;
}) {
  return (
    <Select value={value} onValueChange={(v) => onChange(v as Role)} disabled={disabled}>
      <SelectTrigger className="w-32" aria-label={label}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {ROLES.map((r) => (
          <SelectItem key={r} value={r}>
            {ROLE_LABELS[r]}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function InviteDialog({ tenantId }: { tenantId: string }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('viewer');
  const invite = useMutation({
    mutationFn: () =>
      api<CreatedInvitation>('/invitations', { method: 'POST', body: { email, role } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['invitations', tenantId] }),
  });
  const reset = () => {
    setEmail('');
    setRole('viewer');
    invite.reset();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) reset();
      }}
    >
      <DialogTrigger asChild>
        <Button>Invite member</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Invite a member</DialogTitle>
          <DialogDescription>
            Create a single-use invitation link. It expires in 7 days.
          </DialogDescription>
        </DialogHeader>
        {invite.data ? (
          <div className="grid gap-3">
            <p className="text-sm">
              Share this link with <strong>{invite.data.invitation.email}</strong>. It is shown only
              once.
            </p>
            <CopyField value={invite.data.acceptUrl} label="Invitation link" />
            <DialogFooter>
              <Button onClick={() => setOpen(false)}>Done</Button>
            </DialogFooter>
          </div>
        ) : (
          <form
            className="grid gap-4"
            onSubmit={(e: FormEvent) => {
              e.preventDefault();
              invite.mutate();
            }}
            noValidate
          >
            <ErrorAlert error={invite.error} />
            <div className="grid gap-2">
              <Label htmlFor="invite-email">Email</Label>
              <Input
                id="invite-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="grid gap-2">
              <Label>Role</Label>
              <RoleSelect value={role} onChange={setRole} label="Role" />
            </div>
            <DialogFooter>
              <Button type="submit" disabled={invite.isPending || !email}>
                {invite.isPending ? 'Creating…' : 'Create invitation'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ConfirmButton({
  label,
  title,
  description,
  onConfirm,
}: {
  label: string;
  title: string;
  description: string;
  onConfirm: () => void;
}) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button variant="ghost" size="sm">
          {label}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm}>{label}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function MembersContent({ tenantId, role }: { tenantId: string; role: Role }) {
  const me = useMe();
  const queryClient = useQueryClient();
  const isOwner = hasRole(role, 'owner');
  const members = usePaged<MemberDto>(['members', tenantId], '/members');
  const invitations = usePaged<InvitationDto>(
    ['invitations', tenantId],
    '/invitations',
    hasRole(role, 'admin'),
  );

  const onError = (error: Error) => toast.error(errorMessage(error));
  const changeRole = useMutation({
    mutationFn: (v: { userId: string; role: Role }) =>
      api<MemberDto>(`/members/${v.userId}`, { method: 'PATCH', body: { role: v.role } }),
    onSuccess: () => {
      toast.success('Role updated');
      return queryClient.invalidateQueries({ queryKey: ['members', tenantId] });
    },
    onError,
  });
  const remove = useMutation({
    mutationFn: (userId: string) => api<void>(`/members/${userId}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast.success('Member removed');
      return queryClient.invalidateQueries({ queryKey: ['members', tenantId] });
    },
    onError,
  });
  const revoke = useMutation({
    mutationFn: (id: string) => api<void>(`/invitations/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast.success('Invitation revoked');
      return queryClient.invalidateQueries({ queryKey: ['invitations', tenantId] });
    },
    onError,
  });

  const memberRows = members.data?.pages.flatMap((p) => p.items) ?? [];
  const inviteRows = invitations.data?.pages.flatMap((p) => p.items) ?? [];
  // One search box filters both tables.
  const memberSearch = useListSearch(memberRows, (m) => [
    m.name,
    m.email,
    m.role,
    ROLE_LABELS[m.role],
  ]);
  const inviteSearch = useListSearch(inviteRows, (i) => [i.email, i.role, ROLE_LABELS[i.role]]);
  const setQuery = (q: string) => {
    memberSearch.setQuery(q);
    inviteSearch.setQuery(q);
  };

  return (
    <>
      <PageHeader
        title="Members"
        description="People who can access this tenant."
        actions={isOwner && <InviteDialog tenantId={tenantId} />}
      />
      <ListSearch
        className="mb-4"
        value={memberSearch.query}
        onChange={setQuery}
        label={hasRole(role, 'admin') ? 'Search members and invitations' : 'Search members'}
        placeholder="Search by name, email or role"
      />
      <div className="grid gap-6">
        <Card>
          <CardContent className="pt-6">
            <ErrorAlert error={members.error} />
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Joined</TableHead>
                  {isOwner && <TableHead className="w-24" />}
                </TableRow>
              </TableHeader>
              <TableBody>
                {memberSearch.filtered.map((m) => (
                  <TableRow key={m.userId}>
                    <TableCell className="font-medium">
                      {m.name || '—'}
                      {m.userId === me.user.id && ' (you)'}
                    </TableCell>
                    <TableCell>{m.email}</TableCell>
                    <TableCell>
                      {isOwner ? (
                        <RoleSelect
                          value={m.role}
                          label={`Role for ${m.email}`}
                          disabled={changeRole.isPending}
                          onChange={(next) => changeRole.mutate({ userId: m.userId, role: next })}
                        />
                      ) : (
                        ROLE_LABELS[m.role]
                      )}
                    </TableCell>
                    <TableCell>{formatDate(m.joinedAt)}</TableCell>
                    {isOwner && (
                      <TableCell>
                        <ConfirmButton
                          label="Remove"
                          title={`Remove ${m.email}?`}
                          description="They will immediately lose access to this tenant."
                          onConfirm={() => remove.mutate(m.userId)}
                        />
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {memberRows.length > 0 && memberSearch.filtered.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={isOwner ? 5 : 4}>
                      <NoMatches query={memberSearch.query} />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            {members.hasNextPage && (
              <Button
                variant="outline"
                className="mt-4"
                onClick={() => members.fetchNextPage()}
                disabled={members.isFetchingNextPage}
              >
                Load more
              </Button>
            )}
          </CardContent>
        </Card>

        {hasRole(role, 'admin') && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Pending invitations</CardTitle>
            </CardHeader>
            <CardContent>
              <ErrorAlert error={invitations.error} />
              {inviteRows.length === 0 ? (
                <p className="text-muted-foreground text-sm">No pending invitations.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Email</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Expires</TableHead>
                      {isOwner && <TableHead className="w-24" />}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {inviteSearch.filtered.map((i) => (
                      <TableRow key={i.id}>
                        <TableCell>{i.email}</TableCell>
                        <TableCell>{ROLE_LABELS[i.role]}</TableCell>
                        <TableCell>{formatDate(i.expiresAt)}</TableCell>
                        {isOwner && (
                          <TableCell>
                            <ConfirmButton
                              label="Revoke"
                              title={`Revoke invitation for ${i.email}?`}
                              description="The invitation link will stop working."
                              onConfirm={() => revoke.mutate(i.id)}
                            />
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                    {inviteSearch.filtered.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={isOwner ? 4 : 3}>
                          <NoMatches query={inviteSearch.query} />
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </>
  );
}

export function MembersPage() {
  return (
    <RequireTenant>
      {(tenant) => <MembersContent tenantId={tenant.id} role={tenant.role} />}
    </RequireTenant>
  );
}
